import React, { useMemo, useState } from "react";
import { Check, ArrowRight, Coffee, Users, BedDouble, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { formatIDR, useFx } from "@/i18n/FxProvider";
import { ROOM_HERO, ROOM_GALLERY } from "@/lib/images";
import { SectionTitle, Reveal } from "@/components/Reveal";
import { waUrl, BOOK_MESSAGE } from "@/lib/contact";
import { ROOMS, getCurrentSeason, pricingTierOf, SEASON_WINDOWS } from "@/lib/rooms";

function SeasonBadge({ season, t }) {
  const label = season === "peak" ? t.rooms.peakSeason : season === "high" ? t.rooms.highSeason : t.rooms.lowSeason;
  const colour = season === "low" ? "bg-moss/15 text-moss" : season === "high" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-widest ${colour}`}>
      <Sparkles className="w-3 h-3" /> {label}
    </span>
  );
}

function RoomCard({ room, index, season, withBf, mode, t, lang, fx }) {
  const tier = pricingTierOf(season); // "low" | "high"
  const dailyPrice = withBf ? room.pricing[tier].withBf : room.pricing[tier].noBf;
  const monthlyPrice = room.pricing.monthly[tier];
  const usd = fx?.rate ? Math.round((mode === "daily" ? dailyPrice : monthlyPrice) / fx.rate) : null;
  const reversed = index % 2 === 1;

  const bookText = `Hello Agung Wiwin Homestay,

I would like to inquire about room availability.

Room Type: ${room.name.en}
Rate: ${t.rooms[`${season}Season`]} · ${withBf ? "With breakfast" : "Room only"}
Check-in Date: 
Check-out Date: 
Number of Guests: 

Thank you.`;

  return (
    <article
      data-testid={`room-${room.id}`}
      className={`grid lg:grid-cols-12 gap-10 items-center ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}
    >
      <div className="lg:col-span-7 grid grid-cols-6 gap-3">
        <div className="col-span-6 aspect-[16/10] overflow-hidden rounded-3xl group">
          <img
            src={ROOM_HERO[room.id]}
            alt={room.name.en}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            loading="lazy"
          />
        </div>
        {ROOM_GALLERY[room.id]?.slice(1, 3).map((src, i) => (
          <div key={src + i} className="col-span-3 aspect-[5/4] overflow-hidden rounded-2xl group">
            <img src={src} alt={room.name.en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
        ))}
      </div>

      <div className="lg:col-span-5">
        <div className="eyebrow mb-3">0{index + 1} · Agung Wiwin</div>
        <h3 className="font-display text-3xl sm:text-4xl text-ink leading-tight">{room.name[lang]}</h3>
        <p className="text-moss mt-1.5 text-sm italic">{room.tagline[lang]}</p>

        <p className="text-ink/75 leading-relaxed mt-5 text-[15px]">{room.description[lang]}</p>

        <div className="mt-6 space-y-2 text-sm text-ink/80">
          <div className="flex items-center gap-2"><BedDouble className="w-4 h-4 text-moss" /> {room.bedSize}</div>
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-moss" /> {room.capacity[lang]}</div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2.5">
          {room.facilities[lang].map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm text-ink/80">
              <Check className="w-3.5 h-3.5 text-moss mt-1 shrink-0" /> <span>{f}</span>
            </div>
          ))}
        </div>

        <div className="mt-7 bg-cream rounded-2xl p-5 border border-sand">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-ink/50 mb-1">
                {mode === "daily" ? t.rooms.daily : t.rooms.monthly} · {t.rooms[`${season}Season`]}
                {mode === "daily" && (withBf ? " · " + t.rooms.withBf : " · " + t.rooms.noBf)}
              </div>
              <div className="font-display text-3xl text-ink">
                {formatIDR(mode === "daily" ? dailyPrice : monthlyPrice, lang)}
              </div>
              {usd && (
                <div className="text-xs text-ink/50 mt-1">
                  {t.common.approxUSD} ${usd} USD
                </div>
              )}
            </div>
            <div className="text-right text-xs text-ink/50 leading-tight">
              {mode === "daily" ? "/ " + t.common.perNight.replace("/ ", "") : t.rooms.perMonthNote.replace("/ ", "")}
            </div>
          </div>
        </div>

        <a
          data-testid={`book-${room.id}-btn`}
          href={waUrl(bookText)}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-moss text-cream text-sm hover:bg-ink transition-colors"
        >
          {t.rooms.bookRoom} <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </article>
  );
}

export default function Rooms() {
  const { t, lang } = useLang();
  const fx = useFx();
  const autoSeason = useMemo(() => getCurrentSeason(), []);
  const [season, setSeason] = useState(autoSeason);
  const [withBf, setWithBf] = useState(true);
  const [mode, setMode] = useState("daily"); // "daily" | "monthly"

  return (
    <div className="pb-24" data-testid="rooms-page">
      <section className="container-px mx-auto pt-10">
        <SectionTitle eyebrow={t.rooms.eyebrow} title={t.rooms.title} subtitle={t.rooms.subtitle} />

        {/* Rate controls */}
        <Reveal>
          <div className="mt-10 bg-white border border-sand rounded-3xl p-5 sm:p-7 flex flex-wrap items-center gap-5 sm:gap-8 justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs uppercase tracking-widest text-ink/50">{t.rooms.currentSeason}</span>
              <SeasonBadge season={autoSeason} t={t} />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase tracking-widest text-ink/50 mr-1">{t.rooms.seasonLabel}:</span>
              {["low", "high", "peak"].map((s) => (
                <button
                  key={s}
                  data-testid={`season-${s}-btn`}
                  onClick={() => setSeason(s)}
                  className={`px-3.5 py-1.5 rounded-full text-xs tracking-wide transition-colors ${
                    season === s ? "bg-moss text-cream" : "bg-cream text-ink/70 hover:bg-sand"
                  }`}
                >
                  {t.rooms[`${s}Season`]}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-ink/50 mr-1">{t.rooms.breakfastLabel}:</span>
              <button
                data-testid="bf-with-btn"
                onClick={() => setWithBf(true)}
                className={`px-3.5 py-1.5 rounded-full text-xs tracking-wide transition-colors inline-flex items-center gap-1.5 ${
                  withBf ? "bg-moss text-cream" : "bg-cream text-ink/70 hover:bg-sand"
                }`}
              >
                <Coffee className="w-3 h-3" /> {t.rooms.withBf}
              </button>
              <button
                data-testid="bf-no-btn"
                onClick={() => setWithBf(false)}
                className={`px-3.5 py-1.5 rounded-full text-xs tracking-wide transition-colors ${
                  !withBf ? "bg-moss text-cream" : "bg-cream text-ink/70 hover:bg-sand"
                }`}
              >
                {t.rooms.noBf}
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                data-testid="mode-daily-btn"
                onClick={() => setMode("daily")}
                className={`px-3.5 py-1.5 rounded-full text-xs tracking-wide ${
                  mode === "daily" ? "bg-ink text-cream" : "bg-cream text-ink/70 hover:bg-sand"
                }`}
              >
                {t.rooms.daily}
              </button>
              <button
                data-testid="mode-monthly-btn"
                onClick={() => setMode("monthly")}
                className={`px-3.5 py-1.5 rounded-full text-xs tracking-wide ${
                  mode === "monthly" ? "bg-ink text-cream" : "bg-cream text-ink/70 hover:bg-sand"
                }`}
              >
                {t.rooms.monthly}
              </button>
            </div>
          </div>

          <p className="text-xs text-ink/50 text-center mt-4">{t.rooms.note}</p>
        </Reveal>
      </section>

      <section className="container-px mx-auto mt-16 space-y-24">
        {ROOMS.map((room, i) => (
          <Reveal key={room.id} delay={i * 0.05}>
            <RoomCard room={room} index={i} season={season} withBf={withBf} mode={mode} t={t} lang={lang} fx={fx} />
          </Reveal>
        ))}
      </section>

      {/* Season legend */}
      <section className="container-px mx-auto mt-20">
        <Reveal>
          <div className="bg-moss/5 border border-moss/15 rounded-3xl p-7 md:p-10">
            <div className="eyebrow mb-2 text-moss">Season windows</div>
            <h3 className="font-display text-2xl text-ink mb-5">When each rate applies</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {SEASON_WINDOWS.map((w) => (
                <div key={w.tier} className="bg-white rounded-2xl p-5 border border-sand">
                  <SeasonBadge season={w.tier} t={t} />
                  <div className="mt-3 text-sm text-ink/80">{lang === "id" ? w.rangeId : w.rangeEn}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-ink/60 mt-5 leading-relaxed">
              {lang === "id"
                ? "Pembagian periode ini adalah usulan awal. Hari libur, Nyepi, Paskah, dan tingkat okupansi dapat menjadi pertimbangan penyesuaian harga."
                : "These season windows are suggested periods. Public holidays, Nyepi, Easter, and occupancy may influence final pricing."}
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
